import * as THREE from "three";
import { Tools, isCadTool } from "./tools";

declare const Potree: any;

const SIZE = 240;
const FRAME_INTERVAL = 1000 / 30;
const CURSOR_GAP = 32;

interface ExternalImageMagnifierRendererOptions {
	viewer: any;
	hiddenGroups: THREE.Group[];
	mousePosition: THREE.Vector2;
	snappedPoint: THREE.Vector3;
	getCurrentTool: () => Tools;
	isImageViewActive: () => boolean;
	isCadPointMoveActive: () => boolean;
}

export class ExternalImageMagnifierRenderer {
	private readonly rendererSize = new THREE.Vector2();
	private readonly focusPoint = new THREE.Vector3();
	private readonly previousClearColor = new THREE.Color();
	private readonly groupVisibility: boolean[] = [];
	private context: CanvasRenderingContext2D | null = null;
	private renderState: {
		target: THREE.WebGLRenderTarget;
		pixels: Uint8Array;
		imageData: ImageData;
	} | null = null;
	private isPointerActive = false;
	private hasSnappedPoint = false;
	private lastRender = 0;

	public constructor(private readonly options: ExternalImageMagnifierRendererOptions) {}

	public setTarget(canvas: HTMLCanvasElement | null): void {
		this.context = canvas?.getContext("2d") ?? null;
		if (canvas) canvas.width = canvas.height = SIZE;
		if (!this.context) {
			this.renderState?.target.dispose();
			this.renderState = null;
		}
		this.updateVisibility();
	}

	public setPointerActive(active: boolean): void {
		if (this.isPointerActive === active) return;
		this.isPointerActive = active;
		this.updateVisibility();
	}

	public markSnappedPointAvailable(): void { this.hasSnappedPoint = true; }

	public resetSnappedPoint(): void {
		this.hasSnappedPoint = false;
		this.updateVisibility();
	}

	public deactivate(): void {
		this.isPointerActive = false;
		this.resetSnappedPoint();
	}

	public updateVisibility(visible = this.shouldShow()): void {
		const element = this.getElement();
		if (element) element.style.display = visible ? "block" : "none";
	}

	public updatePosition(): void {
		const element = this.getElement();
		if (!element) return;

		const rect = this.options.viewer.renderer.domElement.getBoundingClientRect();
		if (rect.width === 0 || rect.height === 0) return;

		const radius = SIZE * 0.5;
		const { x, y } = this.options.mousePosition;
		const pointerX = (x + 1) * 0.5 * rect.width;
		const pointerY = (1 - y) * 0.5 * rect.height;
		const lensX = THREE.MathUtils.clamp(pointerX, radius, Math.max(radius, rect.width - radius));
		const lensY = THREE.MathUtils.clamp(pointerY - radius - CURSOR_GAP * 2, radius, Math.max(radius, rect.height - radius));
		element.style.transform = `translate3d(${Math.round(lensX)}px, ${Math.round(lensY)}px, 0) translate(-50%, -50%)`;
	}

	public render = (): void => {
		if (!this.shouldShow()) {
			this.updateVisibility(false);
			return;
		}

		const timestamp = performance.now();
		if (timestamp - this.lastRender < FRAME_INTERVAL) return;
		this.lastRender = timestamp;

		const viewer = this.options.viewer;
		const camera = viewer.scene.getActiveCamera();
		const rendererSize = viewer.renderer.getSize(this.rendererSize);
		const focus = this.focusPoint.copy(this.options.snappedPoint).project(camera);
		if (
			!Number.isFinite(focus.x) || !Number.isFinite(focus.y) || !Number.isFinite(focus.z)
			|| Math.abs(focus.x) > 1 || Math.abs(focus.y) > 1 || focus.z < -1 || focus.z > 1
		) {
			this.updateVisibility(false);
			return;
		}

		const magnifierCamera = camera.clone() as any;
		const focusX = (focus.x + 1) * 0.5 * rendererSize.x;
		const focusY = (1 - focus.y) * 0.5 * rendererSize.y;
		magnifierCamera.setViewOffset(
			rendererSize.x,
			rendererSize.y,
			focusX - SIZE * 0.5,
			focusY - SIZE * 0.5,
			SIZE,
			SIZE,
		);
		magnifierCamera.updateProjectionMatrix();
		magnifierCamera.updateMatrixWorld(true);

		this.updatePosition();
		const context = this.context;
		if (!context) return;
		const { target, pixels, imageData } = this.ensureRenderState(context);
		const previousRenderTarget = viewer.renderer.getRenderTarget();
		const previousClearColor = viewer.renderer.getClearColor(this.previousClearColor);
		const previousClearAlpha = viewer.renderer.getClearAlpha();
		const pointCloudEdlStates = viewer.scene.pointclouds.map((pointcloud: any) => ({
			material: pointcloud.material,
			useEDL: pointcloud.material.useEDL,
		}));

		try {
			this.options.hiddenGroups.forEach((group, index) => {
				this.groupVisibility[index] = group.visible;
				group.visible = false;
			});
			viewer.renderer.setRenderTarget(target);
			viewer.renderer.setClearColor(0x1a1a1a, 1);
			viewer.renderer.clear(true, true, true);
			for (const state of pointCloudEdlStates) state.material.useEDL = false;
			viewer.pRenderer?.render(viewer.scene.scenePointCloud, magnifierCamera, target, {
				clipSpheres: viewer.scene.volumes.filter((volume: any) => volume instanceof Potree.SphereVolume),
			});
			viewer.renderer.setRenderTarget(target);
			viewer.renderer.render(viewer.scene.scene, magnifierCamera);
			viewer.renderer.readRenderTargetPixels(target, 0, 0, SIZE, SIZE, pixels);
			imageData.data.set(pixels);
			context.putImageData(imageData, 0, 0);
			this.drawSnapMarker(context);
			this.updateVisibility(true);
		} finally {
			for (const state of pointCloudEdlStates) state.material.useEDL = state.useEDL;
			this.options.hiddenGroups.forEach((group, index) => group.visible = this.groupVisibility[index]);
			viewer.renderer.setClearColor(previousClearColor, previousClearAlpha);
			viewer.renderer.setRenderTarget(previousRenderTarget);
			viewer.renderer.resetState();
		}
	};

	private shouldShow(): boolean {
		return this.options.isImageViewActive()
			&& this.getElement() != null
			&& this.isPointerActive
			&& this.hasSnappedPoint
			&& (isCadTool(this.options.getCurrentTool()) || this.options.isCadPointMoveActive());
	}

	private getElement(): HTMLElement | null { return this.context?.canvas.parentElement ?? null; }

	private ensureRenderState(context: CanvasRenderingContext2D): NonNullable<typeof this.renderState> {
		if (!this.renderState) {
			this.renderState = {
				target: new THREE.WebGLRenderTarget(SIZE, SIZE),
				pixels: new Uint8Array(SIZE * SIZE * 4),
				imageData: context.createImageData(SIZE, SIZE),
			};
		}
		return this.renderState;
	}

	private drawSnapMarker(context: CanvasRenderingContext2D): void {
		const center = SIZE * 0.5;
		const gap = 14;

		context.save();
		context.beginPath();
		context.moveTo(center, 0);
		context.lineTo(center, center - gap);
		context.moveTo(center, center + gap);
		context.lineTo(center, SIZE);
		context.moveTo(0, center);
		context.lineTo(center - gap, center);
		context.moveTo(center + gap, center);
		context.lineTo(SIZE, center);

		//Dark halo first so the crosshair stays visible over bright backgrounds, then a bright
		//line on top so it stays visible over dark backgrounds.
		context.strokeStyle = "rgba(0, 0, 0, 0.7)";
		context.lineWidth = 3;
		context.stroke();
		context.strokeStyle = "#ffffff";
		context.lineWidth = 1.5;
		context.stroke();
		context.restore();

		context.fillStyle = "#ff0000";
		context.beginPath();
		context.arc(center, center, 3, 0, Math.PI * 2);
		context.fill();
	}
}
